import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db, storage } from '../Firebase';
import { collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Upload, 
  X, 
  Check, 
  Newspaper,
  Calendar,
  Clock,
  Search,
  ExternalLink
} from 'lucide-react';
import Swal from 'sweetalert2';
import { motion, AnimatePresence } from 'framer-motion';
import { fileToBase64 } from '../utils/fileHelper';

const NewsManager = () => {
  const [news, setNews] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    category: 'Update',
    image: null,
    imageUrl: ''
  });

  const categories = ['Update', 'Notice', 'Event', 'News', 'Social Activity'];

  useEffect(() => {
    const q = query(collection(db, 'news'), orderBy('date', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setNews(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 800 * 1024) {
        Swal.fire('Error', 'Image size should not exceed 800KB. Please upload an image below 800KB.', 'error');
        return;
      }
      setFormData({ ...formData, image: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image && !formData.imageUrl) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Cover image is compulsory. Please upload a news cover image.',
        confirmButtonColor: '#0D47A1'
      });
      return;
    }

    setLoading(true);
    try {
      let finalImageUrl = formData.imageUrl;

      if (formData.image) {
        finalImageUrl = await fileToBase64(formData.image);
      }

      const data = {
        title: formData.title,
        description: formData.description,
        date: formData.date,
        category: formData.category,
        imageUrl: finalImageUrl,
        updatedAt: new Date()
      };

      if (editingId) {
        await updateDoc(doc(db, 'news', editingId), data);
        Swal.fire('Success', 'Post updated successfully', 'success');
      } else {
        await addDoc(collection(db, 'news'), { ...data, createdAt: new Date() });
        Swal.fire('Success', 'Post published successfully', 'success');
      }

      resetForm();
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Something went wrong', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ 
      title: '', 
      description: '', 
      date: new Date().toISOString().split('T')[0], 
      category: 'Update', 
      image: null, 
      imageUrl: '' 
    });
    setEditingId(null);
    setIsModalOpen(false);
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: 'Delete Post?',
      text: "This action cannot be undone!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete post'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'news', item.id));
        if (item.imageUrl && item.imageUrl.includes('firebasestorage')) {
          await deleteObject(ref(storage, item.imageUrl));
        }
        Swal.fire('Deleted!', 'Post has been removed.', 'success');
      } catch (error) {
        Swal.fire('Error', 'Failed to delete post', 'error');
      }
    }
  };

  const startEdit = (item) => {
    setFormData({
      title: item.title,
      description: item.description,
      date: item.date,
      category: item.category,
      imageUrl: item.imageUrl,
      image: null
    });
    setEditingId(item.id);
    setIsModalOpen(true);
  };

  const filteredNews = news.filter(item => 
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-primary-dark">News & Updates</h1>
          <p className="text-gray-500 font-medium">Manage corporate news, notices, and upcoming events.</p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-2xl border border-gray-100 shadow-sm focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <Search className="w-5 h-5 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search news..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border-none outline-none text-sm text-gray-700 w-48"
            />
          </div>
          <button
            onClick={() => { resetForm(); setIsModalOpen(true); }}
            className="flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-2xl shadow-lg shadow-primary/20 transition-all active:scale-95 font-bold whitespace-nowrap"
          >
            <Plus className="w-5 h-5" />
            Create Post
          </button>
        </div>
      </div>

      {/* News List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <AnimatePresence>
          {filteredNews.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group hover:shadow-xl transition-all"
            >
              <div className="relative h-56 overflow-hidden">
                <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute top-4 left-4">
                  <span className={`px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-white ${
                    item.category === 'Notice' ? 'bg-red-500' :
                    item.category === 'Event' ? 'bg-amber-500' :
                    item.category === 'News' ? 'bg-blue-500' :
                    item.category === 'Social Activity' ? 'bg-purple-500' : 'bg-emerald-500'
                  }`}>
                    {item.category}
                  </span>
                </div>
                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => startEdit(item)} className="p-2 bg-white text-primary rounded-xl hover:bg-primary hover:text-white shadow-lg transition-all"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(item)} className="p-2 bg-white text-red-500 rounded-xl hover:bg-red-500 hover:text-white shadow-lg transition-all"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                  <Calendar className="w-3 h-3" />
                  {new Date(item.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
                <h3 className="text-xl font-bold text-primary-dark mb-3 line-clamp-2">{item.title}</h3>
                <p className="text-gray-500 text-sm line-clamp-3 leading-relaxed mb-6">{item.description}</p>
                <div className="pt-4 border-t border-gray-50 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-gray-300 uppercase tracking-widest flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Updated {new Date(item.updatedAt?.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <button className="p-2 bg-gray-50 text-gray-400 hover:text-primary rounded-lg transition-colors">
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Modal */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 bg-primary-dark/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-[3rem] shadow-2xl w-full max-w-3xl overflow-hidden max-h-[95vh] overflow-y-auto"
          >
            <div className="p-8 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-primary-dark">{editingId ? 'Edit Post' : 'Create New Post'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Post Title</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    placeholder="Enter main headline..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-bold text-gray-700"
                  >
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Post Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      required
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">Full Description</label>
                  <textarea
                    required
                    rows="4"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none"
                    placeholder="Write your update here..."
                  ></textarea>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 ml-1">Cover Image (Max 800KB)</label>
                <div className="relative h-48 group">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className="h-full border-2 border-dashed border-gray-200 rounded-[2rem] flex flex-col items-center justify-center gap-3 bg-gray-50 group-hover:bg-gray-100 group-hover:border-primary/30 transition-all overflow-hidden">
                    {formData.image || formData.imageUrl ? (
                      <div className="relative w-full h-full">
                        <img 
                          src={formData.image ? URL.createObjectURL(formData.image) : formData.imageUrl} 
                          className="w-full h-full object-cover" 
                          alt="Preview"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Upload className="w-8 h-8 text-white" />
                          <p className="text-white font-bold ml-2">Change Image</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500 font-medium">Click to upload news cover</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4 pb-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-8 py-4 bg-gray-100 text-gray-600 font-bold rounded-2xl hover:bg-gray-200 transition-all active:scale-95"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-[2] px-8 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary-dark shadow-lg shadow-primary/20 transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      {editingId ? 'Update Post' : 'Publish Post'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NewsManager;
